import React, { useCallback } from 'react';
import styled from 'styled-components';

import Menu from 'antd/es/menu';
import Tag from 'antd/es/tag';
import Dropdown from 'antd/es/dropdown';

import { Space } from './layout';
import Button from './Button';
import { Text } from './Text';

const DropdownMenuWrapper = styled.div`
  display: flex;
  flex-direction: ${props => ({ start: 'row-reverse', end: 'row' })[props.align]}}
  flex-wrap: nowrap;
  align-items: center;

  & > :first-child {
    margin-left: ${props => props.align === 'start' ? '8px' : 0};
    margin-right: ${props => props.align === 'end' ? '8px' : 0};
  }
`;

const StyledMenu = styled(Menu)`
  width: fit-content;

  & .ant-dropdown-menu-item-group {
    margin-bottom: 4px;
  }

  & .ant-menu-item-group-list .ant-menu-item {
    margin: 2px 0;
    height: 30px;
    line-height: 32px;
    padding-bottom: 0;
    padding-top: 0;
  }
`;

const StyledTag = styled(Tag)`
  text-transform: capitalize;
  color: #1560AD;
  background: #EFF2F5;
  border: 1px solid #DAE0E6;

  & .name {
    color: #828C94;
    font-size: 12px;
    line-height: 20px;
  }

  & .value {
    color: #828C94;
    font-weight: 600;
    font-size: 13px;
    line-height: 22px;
  }
`;

/* Options ex.
  Options within group
  {
    title: '{option_group}',
    items: [
      { label: '{option_label}', value: '{option_key}' },
    ],
  },
  Option
  { label: '{option_label}', value: '{option_key}' }
 */

const DropdownSelect = ({
  icon,
  options = [],
  value,
  onChange,
  onRemove,
  showSelected = true,
  showSelectedGroup = true,
  allowRemove = true,
  selectedAlign = 'end',
  ...rest
}) => {
  const selectedKeys = Array.isArray(value) ? value : [value];
  const onSelect = useCallback(({ key }) => {
    if (typeof onChange === 'function') {
      onChange(key);
    }
  }, [onChange]);
  
  return (
    <DropdownMenuWrapper align={selectedAlign}>
      <Dropdown
        overlay={() => (
          <StyledMenu
            selectedKeys={selectedKeys}
            onClick={onSelect}
          >
            {
              options.map((option, i) => {
                if (option && Array.isArray(option.items)) {
                  return (
                    <Menu.ItemGroup key={i} title={option.title}>
                      {
                        option.items.map(item => (
                          <Menu.Item key={item.value}>
                            {item.label}
                          </Menu.Item>
                        ))
                      }
                    </Menu.ItemGroup>
                  );
                } else {
                  return (
                    <Menu.Item key={option.value}>
                      {option.label}
                    </Menu.Item>
                  );
                }
              })
            }
          </StyledMenu>
        )}
        placement="bottomRight"
        {...rest}
      >
        <Button type="outlined" shape="circle" icon={icon} />
      </Dropdown>
      {showSelected && (
        <Space>
          {
            selectedKeys.map(selectedKey => {
              const { option, group } = findSelectedOption(selectedKey, options);
              if (!option) {
                return null;
              }

              return (
                <StyledTag
                  key={selectedKey}
                  closable={allowRemove}
                  onClose={() => {
                    if (typeof onRemove === 'function') {
                      onRemove(selectedKey);
                    }
                  }}
                >
                  {showSelectedGroup && (<Text className="name">{group}: </Text>)}
                  <Text className={showSelectedGroup ? 'value' : 'name'}>{option.label}</Text>
                </StyledTag>
              );
            })
          }
        </Space>
      )}
    </DropdownMenuWrapper>
  )
};

const findSelectedOption = (selectedKey, options) => {
  let option = options.find(o => o.value === selectedKey);
  let group = '';
  if (!option) {
    options.filter(o => !!o.items).forEach(o => {
      const found = o.items.find(o => o.value === selectedKey);
      if (found) {
        option = found;
        group = o.title || '';
      };
    });
  }
  return { option, group };
};

export default DropdownSelect;
